import { AxiosError } from 'axios';
import { useMutation, UseMutationOptions } from 'react-query';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Question, QuestionType } from '../data/question';
import { clientState } from '../states/auth';
import {
  behavioralQuestionsState,
  commonQuestionsState,
  hypotheticalQuestionsState,
} from '../states/recording';

interface Params {
  questionType: QuestionType;
}

export function useQuestionByType(
  options?: UseMutationOptions<Question[], AxiosError, Params>
) {
  const setCommonQuestions = useSetRecoilState(commonQuestionsState);
  const setBehavioralQuestions = useSetRecoilState(behavioralQuestionsState);
  const setHypotheticalQuestions = useSetRecoilState(
    hypotheticalQuestionsState
  );

  const client = useRecoilValue(
    clientState({
      contentType: 'application/json',
      apiVersion: 1,
    })
  );

  const getApiEndpoint = (questionType: QuestionType): string => {
    switch (questionType) {
      case 'common':
        return '/questions/interviewAcademy/common';
      case 'behavioral':
        return '/questions/interviewAcademy/behavioral';
      case 'hypothetical':
        return '/questions/interviewAcademy/hypothetical';
      default:
        return '/questions/interviewAcademy/common';
    }
  };

  return useMutation<Question[], AxiosError, Params>(
    async params => {
      const endpoint = getApiEndpoint(params.questionType);
      const response = await client.get(endpoint);

      if (!response.data || !response.data.success || !response.data.data) {
        throw new Error(
          'Invalid API response: missing success or data properties'
        );
      }

      if (!Array.isArray(response.data.data)) {
        throw new Error(
          'Invalid API response: expected array of questions in data property'
        );
      }

      return response.data.data;
    },
    {
      ...options,
      onSuccess(questions, params) {
        // Set the appropriate state based on question type
        switch (params.questionType) {
          case 'common':
            setCommonQuestions(questions);
            break;
          case 'behavioral':
            setBehavioralQuestions(questions);
            break;
          case 'hypothetical':
            setHypotheticalQuestions(questions);
            break;
          default:
            setCommonQuestions(questions);
            break;
        }

        // Call the original onSuccess if provided
        if (options?.onSuccess) {
          options.onSuccess(questions, params, undefined);
        }
      },
    }
  );
}
